/**
 * Secure localStorage wrapper with integrity checking
 * Uses a simple hash to detect tampering with stored values
 */

// Changing this version will invalidate all previously stored values
const STORAGE_VERSION = '1.0';

// Generate a simple hash for integrity checking
// This is not cryptographic security, but detects casual tampering
async function generateHash(value: string, timestamp: number, salt: string): Promise<string> {
    const data = new TextEncoder().encode(STORAGE_VERSION + value + timestamp + salt);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));

    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

// Get a fingerprint of the current browser session for additional validation
function getBrowserFingerprint(): string {
    const parts = [
        navigator.userAgent,
        navigator.language,
        screen.colorDepth.toString(),
        new Date().getTimezoneOffset().toString(),
    ];

    return parts.join('|');
}

// Salt for hashing - unique per browser but consistent across tabs
function getSalt(): string {
    let salt = localStorage.getItem('_storage_salt');

    if(!salt) {
        const array = new Uint8Array(16);

        crypto.getRandomValues(array);
        salt = Array.from(array).map((b) => b.toString(16).padStart(2, '0')).join('');
        localStorage.setItem('_storage_salt', salt);
    }

    return salt + getBrowserFingerprint();
}

export interface SecureStorageValue<T> {
    value: T;
    hash: string;
    timestamp: number;
}

export async function setSecureItem<T>(key: string, value: T): Promise<void> {
    const valueString = JSON.stringify(value);
    const salt = getSalt();
    const timestamp = Date.now();
    const hash = await generateHash(valueString, timestamp, salt);

    const storageValue: SecureStorageValue<T> = {
        value,
        hash,
        timestamp,
    };

    localStorage.setItem(key, JSON.stringify(storageValue));
}

export async function getSecureItem<T>(key: string): Promise<T | null> {
    try {
        const stored = localStorage.getItem(key);

        if(!stored) return null;

        const storageValue: SecureStorageValue<T> = JSON.parse(stored);
        const valueString = JSON.stringify(storageValue.value);
        const salt = getSalt();
        const expectedHash = await generateHash(valueString, storageValue.timestamp, salt);

        if(storageValue.hash !== expectedHash) {
            console.warn(`Integrity check failed for key: ${key}`);
            removeSecureItem(key);

            return null;
        }

        return storageValue.value;
    } catch (e) {
        removeSecureItem(key);

        return null;
    }
}

export function removeSecureItem(key: string): void {
    localStorage.removeItem(key);
}

export function isValidId(id: string): boolean {
    if(!id || typeof id !== 'string') {
        return false;
    }

    const hexPattern = /^[a-f0-9]{32}$/i;

    return hexPattern.test(id);
}
